import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
}

const DEFAULT_TITLE = 'ClubIQ - AI-Powered Golf Club Fitting & Recommendations';
const DEFAULT_DESCRIPTION = 'Get personalized golf club recommendations based on your swing, build, and skill level. AI-powered fitting technology matches you with the perfect clubs for your game. Free online golf club fitting tool.';
const DEFAULT_KEYWORDS = 'golf club fitting, AI golf recommendations, golf club selector, best golf clubs, personalized golf equipment, golf bag optimization, golf distance calculator, handicap improvement';
const DEFAULT_IMAGE = 'https://www.clubiq.golf/Images/New Logo Image.png';
const SITE_URL = 'https://www.clubiq.golf';

export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  noIndex = false,
}: SEOProps) {
  const fullTitle = title ? `${title} | ClubIQ` : DEFAULT_TITLE;
  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="ClubIQ" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Additional SEO Tags */}
      <meta name="author" content="ClubIQ" />
      <meta name="application-name" content="ClubIQ" />
      <meta name="theme-color" content="#22c55e" />
    </Helmet>
  );
}

// Page-specific SEO configurations
export const PAGE_SEO = {
  home: {
    title: 'AI-Powered Golf Club Fitting & Recommendations',
    description: 'Get personalized golf club recommendations based on your swing, build, and skill level. Free AI-powered fitting technology matches you with the perfect clubs for your game.',
    keywords: 'golf club fitting, AI golf recommendations, golf club selector, best golf clubs 2024, personalized golf equipment',
  },
  myGolfBag: {
    title: 'My Golf Bag',
    description: 'Track your golf clubs, distances, and equipment. Get AI-powered insights to optimize your bag and improve your game.',
    keywords: 'golf bag tracker, club distances, golf equipment manager, my golf clubs',
  },
  clubOptimizer: {
    title: 'Club Optimizer - Bag Health Check',
    description: 'Analyze your golf bag for gaps, weak spots, and optimization opportunities. Get AI recommendations to improve your equipment setup.',
    keywords: 'golf bag analysis, club gapping, golf equipment optimization, bag health check',
  },
  recommendations: {
    title: 'Club Recommendations',
    description: 'Get personalized golf club recommendations tailored to your swing speed, skill level, and budget. AI-powered fitting suggestions.',
    keywords: 'golf club recommendations, personalized golf clubs, best clubs for my game, club fitting suggestions',
  },
  equipmentFitting: {
    title: 'Equipment Fitting',
    description: 'Find the perfect golf clubs for your game with our AI-powered fitting tool. Matches shaft flex, club head, and specifications to your swing.',
    keywords: 'golf club fitting online, shaft fitting, club head selection, custom golf clubs',
  },
  distanceCalculator: {
    title: 'Distance Calculator',
    description: 'Calculate your golf club distances based on swing speed, conditions, and club specifications. Accurate yardage predictions.',
    keywords: 'golf distance calculator, club yardage, swing speed distance, golf shot distance',
  },
  shotAdvisor: {
    title: 'Shot Advisor',
    description: 'Get AI-powered shot recommendations based on distance, conditions, and your club setup. Make smarter decisions on the course.',
    keywords: 'golf shot advisor, course strategy, club selection, golf AI caddie',
  },
  swingAnalyzer: {
    title: 'Swing Tips & Analysis',
    description: 'Get personalized swing tips and analysis based on your skill level and common issues. Improve your golf swing with AI guidance.',
    keywords: 'golf swing tips, swing analysis, improve golf swing, swing mechanics',
  },
  ballMatching: {
    title: 'Golf Ball Matching',
    description: 'Find the perfect golf ball for your game. Match ball characteristics to your swing speed, spin preference, and playing style.',
    keywords: 'golf ball fitting, best golf ball, ball matching, golf ball selector',
  },
  encyclopedia: {
    title: 'Golf Equipment Encyclopedia',
    description: 'Comprehensive guide to golf equipment, club types, shaft specifications, and more. Learn everything about golf gear.',
    keywords: 'golf equipment guide, club types, shaft flex guide, golf gear encyclopedia',
  },
  beginnerBuilder: {
    title: 'Beginner Bag Builder',
    description: 'Build the perfect starter golf bag. Get recommendations for beginner-friendly clubs that help you learn and improve faster.',
    keywords: 'beginner golf clubs, starter golf set, first golf bag, golf for beginners',
  },
  profile: {
    title: 'My Profile',
    description: 'Manage your ClubIQ profile, preferences, and golf equipment data.',
    keywords: 'golf profile, user settings, golf preferences',
  },
  about: {
    title: 'About ClubIQ',
    description: 'Learn about ClubIQ - the AI-powered golf assistant helping golfers find the perfect equipment for their game.',
    keywords: 'about clubiq, golf AI company, golf technology',
  },
  contact: {
    title: 'Contact Us',
    description: 'Get in touch with the ClubIQ team. We\'re here to help with your golf equipment questions and feedback.',
    keywords: 'contact clubiq, golf support, golf help',
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'ClubIQ privacy policy: what we collect, how we use AI, which processors receive data, and how to delete your account.',
    keywords: 'privacy policy, data protection, golf app privacy',
  },
  terms: {
    title: 'Terms of Service',
    description: 'ClubIQ terms of service and conditions of use.',
    keywords: 'terms of service, user agreement, golf app terms',
  },
};

